import { memo, useState } from 'react';
import { ChevronRight, ChevronDown, Copy, Check, ClipboardList } from 'lucide-react';
import { Badge } from '@/components/ui';
import type { RunGroupItem, SubAgentGroup } from './useEventGrouping';
import { getLatestNonDeltaEvent } from './useEventGrouping';
import { getEventPreviewText } from './event-renderers';
import type { PlatformEvent } from '../../types/events';
import EventCard from './EventCard';
import SubAgentGroupCard from './SubAgentGroupCard';

function formatTimestamp(ts: string): string {
  const d = new Date(ts);
  return d.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

const statusConfig = {
  completed: { label: 'completed', variant: 'success' as const },
  failed: { label: 'failed', variant: 'error' as const },
  running: { label: 'running', variant: 'warning' as const },
  cancelled: { label: 'cancelled', variant: 'warning' as const },
};

function isSubAgentGroup(item: PlatformEvent | SubAgentGroup): item is SubAgentGroup {
  return (item as SubAgentGroup).kind === 'subagent-group';
}

function RunGroupCardInner({ group }: { group: RunGroupItem }) {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copiedEvents, setCopiedEvents] = useState(false);
  const config = statusConfig[group.status];
  const source = group.events[0]?.source ?? 'unknown';
  const subAgentCount = group.subAgentGroups.length;
  const previewEvent = getLatestNonDeltaEvent(group.events);

  return (
    <div data-testid="run-group-card" className="rounded-lg border border-l-2 border-l-accent-run-border/40 bg-card p-3">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-3 hover:bg-accent/50 rounded transition-colors text-left"
      >
        {expanded ? (
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
        )}
        <span className="font-mono text-xs text-muted-foreground whitespace-nowrap">
          {formatTimestamp(group.latestTimestamp)}
        </span>
        <Badge variant="info">{source}</Badge>
        <span className="text-xs text-muted-foreground truncate">
          {group.agentName && (
            <span className="text-foreground font-medium">{group.agentName} &middot; </span>
          )}
          Run: {group.subjectId}
        </span>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            navigator.clipboard.writeText(group.subjectId);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          }}
          className="p-1 rounded hover:bg-accent text-muted-foreground hover:text-foreground"
          title="Copy run ID"
        >
          {copied ? (
            <Check className="h-3.5 w-3.5" />
          ) : (
            <Copy className="h-3.5 w-3.5" />
          )}
        </button>
        <Badge variant={config.variant}>
          {group.status === 'running' && (
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-yellow-500 animate-pulse mr-1" />
          )}
          {config.label}
        </Badge>
        {subAgentCount > 0 && (
          <Badge variant="purple">
            {subAgentCount} sub-agent{subAgentCount !== 1 ? 's' : ''}
          </Badge>
        )}
        <span className="text-xs text-muted-foreground ml-auto whitespace-nowrap flex items-center gap-1">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              const payload = {
                runId: group.subjectId,
                agentId: group.events[0]?.data?.agentId,
                agentName: group.agentName,
                status: group.status,
                eventCount: group.events.length,
                firstEventTimestamp: group.events[group.events.length - 1]?.metadata.timestamp,
                lastEventTimestamp: group.events[0]?.metadata.timestamp,
                subAgentCount: group.subAgentGroups.length,
                events: group.events,
              };
              navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
              setCopiedEvents(true);
              setTimeout(() => setCopiedEvents(false), 1500);
            }}
            className="p-1 rounded hover:bg-accent text-muted-foreground hover:text-foreground"
            title="Copy all run events"
          >
            {copiedEvents ? (
              <Check className="h-3.5 w-3.5" />
            ) : (
              <ClipboardList className="h-3.5 w-3.5" />
            )}
          </button>
          {group.events.length} event{group.events.length !== 1 ? 's' : ''}
        </span>
      </button>

      {/* Collapsed preview */}
      {!expanded && previewEvent && (
        <p className="ml-7 text-xs text-muted-foreground truncate">
          {getEventPreviewText(previewEvent)}
        </p>
      )}

      {/* Expanded: interleaved timeline of parent events and sub-agent groups */}
      {expanded && (
        <div className="ml-4 mt-6 mb-1 pb-3 pl-2 space-y-2">
          {group.timelineItems.map((item) =>
            isSubAgentGroup(item) ? (
              <SubAgentGroupCard key={`sa-${item.runId}`} group={item} />
            ) : (
              <EventCard key={item.eventId} event={item} />
            )
          )}
        </div>
      )}
    </div>
  );
}

const RunGroupCard = memo(RunGroupCardInner);
export default RunGroupCard;
